// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Satellites layer in a shared session (rooms, meetings, the shared AR
 * marker): the lead's choices, its satellite clock and its selected object
 * go to everyone, so the whole room sees the same swarm at the same moment.
 * Every device works out the positions itself from the same data and time.
 * Followers draw the lead's choices without saving them as their own.
 * "Sky above me" stays each viewer's own: it is their sky.
 */
import type { RoomSatellites } from '../roomProtocol'
import type { SatelliteLayer, SatelliteSettings, ShowSet } from './satelliteLayer'

/** What the lead sends; undefined while its satellites are off. */
export function roomSatellitesOf(layer: SatelliteLayer): RoomSatellites | undefined {
  if (!layer.on) return undefined
  const s = layer.settings
  const c = layer.clock
  const selected = layer.selectedInfo?.norad
  return {
    show: [...s.show],
    colourBy: s.colourBy,
    ...(s.hidden.length ? { hidden: [...s.hidden] } : {}),
    altitude: s.altitude,
    trails: s.trails,
    live: c.live,
    ...(c.live ? {} : { simMs: Math.round(c.time()), speed: c.speed, playing: c.playing }),
    ...(selected !== undefined ? { selected } : {}),
  }
}

/** What counts as a change worth sending: everything but the clock's steady advance. */
export function roomSatellitesKey(s: RoomSatellites | undefined): string {
  if (!s) return ''
  const { simMs: _simMs, ...rest } = s
  return JSON.stringify(rest)
}

/** Where the lead's sim time is now, `elapsedMs` after it said `s`. */
export function leadSimMs(s: RoomSatellites, elapsedMs: number, now = Date.now()): number {
  if (s.live || s.simMs === undefined) return now
  return s.simMs + (s.playing === false ? 0 : elapsedMs * (s.speed ?? 1))
}

/**
 * A follower: show the lead's satellites (null: the lead shows none, so
 * none here either). `receivedAt` is when the lead's state came in.
 * No-op when they already match; the clock is moved only when it drifts.
 */
export function applyRoomSatellites(layer: SatelliteLayer, s: RoomSatellites | null, receivedAt: number, now = Date.now()): void {
  if (!s) {
    if (layer.on) layer.update({ on: false }, { save: false })
    return
  }
  const patch: Partial<SatelliteSettings> = {
    on: true,
    show: s.show as ShowSet[],
    colourBy: s.colourBy,
    hidden: s.hidden ?? [],
    altitude: s.altitude,
    trails: s.trails,
  }
  const cur = layer.settings
  const differs =
    !cur.on ||
    JSON.stringify(cur.show) !== JSON.stringify(patch.show) ||
    cur.colourBy !== patch.colourBy ||
    JSON.stringify(cur.hidden) !== JSON.stringify(patch.hidden) ||
    cur.altitude !== patch.altitude ||
    cur.trails !== patch.trails
  if (differs) layer.update(patch, { save: false })

  const clock = layer.clock
  if (s.live) {
    if (!clock.live) clock.goLive()
  } else {
    const want = leadSimMs(s, now - receivedAt, now)
    const speed = s.speed ?? 1
    const playing = s.playing !== false
    if (clock.live || clock.speed !== speed || clock.playing !== playing || Math.abs(clock.time() - want) > 3000) {
      clock.apply({ live: false, playing, speed, anchorSimMs: want, anchorRealMs: now })
    }
  }

  const sel = s.selected ?? null
  if ((layer.selectedInfo?.norad ?? null) !== sel) layer.selectNorad(sel)
}
